import React, { useCallback, useEffect, useState } from 'react';
import type { InvoiceDto, IssuerProfileDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { euro } from '../format';
import { Empty, ErrorBanner, Field, Modal, Panel, Stat } from '../ui';
import { ask } from '../dialogs';

interface Collections {
  open: InvoiceDto[];
  paidThisMonthCents: number;
}

const dateIt = (d: string | null) => (d ? new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('it-IT') : '—');
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dueOf = (i: InvoiceDto): string | null => {
  if (i.dueDate) return i.dueDate;
  if (!i.issueDate) return null;
  const d = new Date(i.issueDate + 'T12:00:00');
  d.setDate(d.getDate() + 30);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysLate = (i: InvoiceDto): number => {
  const due = dueOf(i);
  if (!due) return 0;
  return Math.floor((new Date(todayKey() + 'T12:00:00').getTime() - new Date(due + 'T12:00:00').getTime()) / 86_400_000);
};

/** Cliente della fattura: quello in anagrafica, altrimenti il destinatario scritto a mano. */
const clientKey = (i: InvoiceDto) => (i.clientId ? `c${i.clientId}` : `r${i.recipientName.trim().toLowerCase()}`);
const clientLabel = (i: InvoiceDto) => i.clientName ?? i.recipientName;
const localKey = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Incassi e solleciti: fatture emesse ancora da incassare, con promemoria di pagamento al cliente. */
export function CollectionsPage({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [data, setData] = useState<Collections | null>(null);
  const [paid, setPaid] = useState<InvoiceDto[]>([]);
  const [filter, setFilter] = useState<'all' | 'late'>('all');
  const [client, setClient] = useState('');
  const [reminding, setReminding] = useState<InvoiceDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, all] = await Promise.all([api.get<Collections>('/api/collections'), api.get<InvoiceDto[]>('/api/invoices').catch(() => [] as InvoiceDto[])]);
      setData(c);
      setPaid(all.filter((i) => i.status === 'paid' && i.paidAt));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (ctx.event?.type === 'app.notice') void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.eventSeq]);

  const markPaid = async (i: InvoiceDto) => {
    if (!(await ask(`Segnare come incassata la fattura ${i.number} (${euro(i.totalCents)})?`))) return;
    try {
      await api.post(`/api/invoices/${i.id}/status`, { status: 'paid' });
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Elenco dei clienti (da incassare o già incassati), in ordine alfabetico
  const clients = new Map<string, string>();
  for (const i of [...(data?.open ?? []), ...paid]) if (!clients.has(clientKey(i))) clients.set(clientKey(i), clientLabel(i));
  const clientList = [...clients.entries()].sort((a, b) => a[1].localeCompare(b[1], 'it'));
  const mine = (i: InvoiceDto) => !client || clientKey(i) === client;

  const open = (data?.open ?? []).filter(mine);
  const lateList = open.filter((i) => daysLate(i) > 0);
  const visible = filter === 'late' ? lateList : open;
  const sum = (l: InvoiceDto[]) => l.reduce((n, i) => n + i.totalCents, 0);
  const today = todayKey();
  const paidMine = paid.filter(mine).sort((a, b) => (b.paidAt ?? '').localeCompare(a.paidAt ?? ''));
  const paidMonth = client ? sum(paidMine.filter((i) => localKey(i.paidAt!).startsWith(today.slice(0, 7)))) : (data?.paidThisMonthCents ?? 0);
  const paidYear = sum(paidMine.filter((i) => localKey(i.paidAt!).startsWith(today.slice(0, 4))));

  return (
    <div className="panels single">
      <Panel
        title="Incassi"
        subtitle={client ? `Cliente: ${clients.get(client)}` : 'Fatture emesse da incassare'}
        right={
          <div className="coll-filters">
            <select value={client} onChange={(e) => setClient(e.target.value)} title="Scegli un cliente per vedere solo i suoi incassi">
              <option value="">Tutti i clienti</option>
              {clientList.map(([k, n]) => (
                <option key={k} value={k}>
                  {n}
                </option>
              ))}
            </select>
            <select value={filter} title="Tutte o solo quelle già scadute" onChange={(e) => setFilter(e.target.value as 'all' | 'late')}>
              <option value="all">Tutte da incassare</option>
              <option value="late">Solo in ritardo</option>
            </select>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {data && (
          <div className="stats-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Stat value={euro(sum(open))} label="Da incassare" navy />
            <Stat value={euro(sum(lateList))} label={`In ritardo (${lateList.length})`} />
            <Stat value={euro(paidMonth)} label="Incassato questo mese" />
            <Stat value={euro(paidYear)} label={`Incassato nel ${today.slice(0, 4)}`} />
          </div>
        )}
        {!data ? (
          <div className="muted small">Caricamento…</div>
        ) : visible.length === 0 ? (
          <Empty icon="check">{filter === 'late' ? 'Nessuna fattura in ritardo.' : 'Niente da incassare.'}</Empty>
        ) : (
          <div className="ag-list">
            {visible.map((i) => {
              const late = daysLate(i);
              return (
                <div key={i.id} className="pay-card">
                  <div className="row-actions" style={{ justifyContent: 'space-between', margin: 0 }}>
                    <b>
                      {i.number} · {i.clientName ?? i.recipientName}
                    </b>
                    <b>{euro(i.totalCents)}</b>
                  </div>
                  <div className="muted small">
                    {i.subject}
                    {ctx.me.user.role === 'admin' && ` · emessa da ${i.createdByName}`}
                  </div>
                  <div className="small">
                    Scadenza {dateIt(dueOf(i))}
                    {late > 0 ? <span className="kan-due late"> · {late} {late === 1 ? 'giorno' : 'giorni'} di ritardo</span> : ''}
                    {i.remindersCount > 0 && <span className="muted"> · sollecitata {i.remindersCount}× (ultima {dateIt(i.lastReminderAt)})</span>}
                  </div>
                  {i.clientPaidNoticeAt && <div className="note good">Il cliente segnala di aver pagato il {dateIt(i.clientPaidNoticeAt)}: verifica e conferma.</div>}
                  <div className="row-actions" style={{ margin: 0 }}>
                    <button type="button" className="btn small" title="Ricorda al cliente di pagare: avviso nella sua area e, se vuoi, email" onClick={() => setReminding(i)}>
                      <Icon name="bell" size={13} /> Sollecita
                    </button>
                    <button type="button" className="btn small dark" title="Il cliente ha pagato" onClick={() => void markPaid(i)}>
                      <Icon name="check" size={13} /> Incassata
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {client && paidMine.length > 0 && (
          <>
            <h3 className="coll-paid-title">Già incassate ({paidMine.length})</h3>
            <div className="ag-list">
              {paidMine.map((i) => (
                <div key={i.id} className="pay-card">
                  <div className="row-actions" style={{ justifyContent: 'space-between', margin: 0 }}>
                    <b>{i.number}</b>
                    <b>{euro(i.totalCents)}</b>
                  </div>
                  <div className="muted small">
                    {i.subject} · incassata il {dateIt(i.paidAt)}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Panel>
      {reminding && (
        <RemindModal
          ctx={ctx}
          invoice={reminding}
          late={daysLate(reminding)}
          onClose={() => setReminding(null)}
          onDone={() => {
            setReminding(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function RemindModal({ ctx, invoice: i, late, onClose, onDone }: { ctx: Ctx; invoice: InvoiceDto; late: number; onClose: () => void; onDone: () => void }) {
  const { api } = ctx;
  const [profile, setProfile] = useState<IssuerProfileDto | null>(null);
  const [email, setEmail] = useState(true);
  const [to, setTo] = useState(i.recipientEmail ?? '');
  const [subject, setSubject] = useState(`Promemoria di pagamento - fattura ${i.number}`);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = ctx.me.user.role === 'admin';

  useEffect(() => {
    api.get<{ profile: IssuerProfileDto | null }>('/api/invoices/profile').then((r) => setProfile(r.profile)).catch(() => undefined);
  }, [api]);

  useEffect(() => {
    const due = dateIt(i.dueDate);
    setText(
      `Buongiorno,\n\ncon la presente ricordiamo che la fattura ${i.number} di ${euro(i.totalCents)}${i.dueDate ? ` con scadenza ${due}` : ''}${
        late > 0 ? ' risulta ancora da saldare' : ' è in scadenza'
      }.${profile?.iban ? `\n\nCoordinate per il bonifico: ${profile.iban} (causale: fattura ${i.number}).` : ''}\n\nSe ha già provveduto al pagamento, La preghiamo di ignorare questo messaggio.\n\nCordiali saluti\n${ctx.me.user.name}`,
    );
  }, [profile, i, late, ctx.me.user.name]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/api/collections/${i.id}/remind`, { sendEmail: email, to: email ? to.split(/[,;\s]+/).filter(Boolean) : [], subject, text });
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;

  return (
    <Modal
      title={`Sollecito · fattura ${i.number}`}
      onClose={onClose}
      footer={
        <div className="quote-actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="button" className="btn dark" onClick={() => void send()} disabled={busy}>
            {busy ? 'Invio…' : email ? 'Invia sollecito' : 'Registra sollecito'}
          </button>
        </div>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <p className="small">Il cliente vede sempre l’avviso nella sua area; l’email con il PDF è in più.</p>
      <label className="ag-allday">
        <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Invia anche email dalla mia casella
      </label>
      {isAdmin && email && (
        <div className="note warn small">L’email parte dalla tua casella in Posta: se non l’hai collegata, usa «Apri nel programma di posta».</div>
      )}
      {email && (
        <>
          <Field label="A">
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="email del cliente" />
          </Field>
          <Field label="Oggetto">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
        </>
      )}
      <Field label="Testo">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} />
      </Field>
      <a className="btn small" href={mailto} title="Apre il tuo programma di posta con il testo già scritto">
        <Icon name="mail" size={13} /> Apri nel programma di posta
      </a>
    </Modal>
  );
}
